import type React from "react"
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { T } from "@/i18n"
import {
  useGetEmailTemplate,
  useCreateEmailTemplate,
  useUpdateEmailTemplate
} from "@/hooks/email-templates"
import {
  ENTITY_TYPES,
  TEMPLATE_TYPES,
  ENTITY_FIELDS,
  COMMON_FIELDS,
} from "@/models/email-template"
import { LoadingSpinner } from "@/components/loading-spinner"
import { EmailBuilder } from "./email-builder"
import { ArrowLeft, Save } from "lucide-react"

type EmailTemplateFormData = {
  textContent: string
  name: string
  subject: string
  entityType: string
  templateType: string
  htmlContent: string
}

const initialFormData: EmailTemplateFormData = {
  name: "",
  subject: "",
  entityType: "Accommodation",
  templateType: "hotel-booking-request",
  textContent: "",
  htmlContent: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
    <h2>Room Booking Request</h2>
    <p>Dear {{hotel.name}} Team,</p>
    <p>We would like to request a room booking for our client:</p>
    <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
      <p><strong>Client:</strong> {{client.name}}</p>
      <p><strong>Check-in:</strong> {{accommodation.checkInDate}}</p>
      <p><strong>Check-out:</strong> {{accommodation.checkOutDate}}</p>
      <p><strong>Room Type:</strong> {{accommodation.requestedType}}</p>
      <p><strong>Number of People:</strong> {{accommodation.headCount}}</p>
      <p><strong>Special Notes:</strong> {{accommodation.notes}}</p>
    </div>
    <p>Please confirm this booking by clicking the link below:</p>
    <p><a href="https://hooks.tourmanager.pro/email/confirm/{{triggerEvent.id}}" style="display: inline-block; background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Confirm Booking</a></p>
    <p>If you need to decline or have questions, please <a href="https://hooks.tourmanager.pro/email/decline/{{triggerEvent.id}}">click here</a> or reply to this email.</p>
    <p>Thank you for your cooperation.</p>
    <p>Best regards,<br>{{companyName}} Team</p>
  </div>`,
}

export function TemplateForm({
  templateId,
  onSuccess,
  onBack
}: {
  templateId: string | null
  onBack : () => void
  onSuccess: () => void
}) {
  const [formData, setFormData] = useState<EmailTemplateFormData>(initialFormData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availableFields, setAvailableFields] = useState<Array<{ name: string; label: string }>>([])

  const { data: template, isLoading: isLoadingTemplate } = useGetEmailTemplate(templateId || "", {
    enabled: !!templateId,
  })

  const { mutate: createTemplate } = useCreateEmailTemplate({
    onSuccess: () => {
      setIsSubmitting(false)
      onSuccess()
      onBack()
    },
    // onError: () => {
    //   setIsSubmitting(false)
    // },
  })

  const { mutate: updateTemplate } = useUpdateEmailTemplate({
    onSuccess: () => {
      setIsSubmitting(false)
      onSuccess()
      onBack()
    },
    // onError: () => {
    //   setIsSubmitting(false)
    // },
  })

  useEffect(() => {
    if (template) {
      // Extract entity type and template type from the template data
      // This assumes the template.type is stored as "entityType:templateType"
      const [entityType, templateType] = template.type.split(":")

      setFormData({
        name: template.name,
        subject: template.subject,
        entityType: entityType || "Accommodation",
        templateType: templateType || template.type,
        htmlContent: template.htmlContent,
        textContent: template.textContent
      })
    }
  }, [template])

  useEffect(() => {
    // Update available fields when entity type changes
    const entityFields = ENTITY_FIELDS[formData.entityType as keyof typeof ENTITY_FIELDS] || []

    // Add triggerEvent fields for RSVP URLs and response data
    // const triggerEventFields = [
    //   { name: "triggerEvent.id", label: "Trigger Event ID (for RSVP URLs)" },
    //   { name: "triggerEvent.notes", label: "Response Notes" },
    //   { name: "triggerEvent.status", label: "Response Status" },
    //   { name: "triggerEvent.price", label: "Updated Price" },
    //   { name: "BASE_URL", label: "Base URL (for internal links)" },
    // ]

    setAvailableFields([...entityFields, ...COMMON_FIELDS])

    // Update template type if the current one is not valid for the selected entity
    const validTemplateTypes = TEMPLATE_TYPES[formData.entityType as keyof typeof TEMPLATE_TYPES] || []
    const isValidType = validTemplateTypes.some((t) => t.value === formData.templateType)

    if (!isValidType && validTemplateTypes.length > 0) {
      setFormData((prev) => ({
        ...prev,
        templateType: validTemplateTypes[0].value,
      }))
    }
  }, [formData.entityType])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Update the handleEntityTypeChange function to include all template types
  const handleEntityTypeChange = (value: string) => {
    // Set default template content based on entity type
    let defaultHtmlContent = ""

    if (value === "Accommodation") {
      if (
        formData.templateType === "hotel-booking-confirmation" ||
        (!templateId && formData.templateType === "hotel-booking-confirmation")
      ) {
        // Hotel booking confirmation template
        defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>Hotel Booking Response Notification</h2>
          <p>Dear Team Member,</p>
          <p>We have received a response from the hotel regarding a booking request:</p>
          <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
            <p><strong>Hotel:</strong> {{hotel.name}}</p>
            <p><strong>Response:</strong> <span style="color: #4CAF50; font-weight: bold;">Confirmed</span></p>
            <p><strong>Client:</strong> {{client.name}}</p>
            <p><strong>Check-in:</strong> {{accommodation.checkInDate}}</p>
            <p><strong>Check-out:</strong> {{accommodation.checkOutDate}}</p>
            <p><strong>Room Type:</strong> {{accommodation.requestedType}}</p>
            <p><strong>Number of People:</strong> {{accommodation.headCount}}</p>
            <p><strong>Price:</strong> {{triggerEvent.price}}</p>
            <p><strong>Hotel Notes:</strong> {{triggerEvent.notes}}</p>
          </div>
          <p>Please update the reservation status in the system and notify the client if necessary.</p>
          <p><a href="{{BASE_URL}}/reservations/{{reservation.id}}" style="display: inline-block; background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">View Reservation</a></p>
          <p>Best regards,<br>{{companyName}} System</p>
        </div>`
      } else if (
        formData.templateType === "hotel-booking-declined" ||
        (!templateId && formData.templateType === "hotel-booking-declined")
      ) {
        // Hotel booking declined template
        defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>Hotel Booking Response Notification</h2>
          <p>Dear Team Member,</p>
          <p>We have received a response from the hotel regarding a booking request:</p>
          <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
            <p><strong>Hotel:</strong> {{hotel.name}}</p>
            <p><strong>Response:</strong> <span style="color: #FF5252; font-weight: bold;">Declined</span></p>
            <p><strong>Client:</strong> {{client.name}}</p>
            <p><strong>Check-in:</strong> {{accommodation.checkInDate}}</p>
            <p><strong>Check-out:</strong> {{accommodation.checkOutDate}}</p>
            <p><strong>Room Type:</strong> {{accommodation.requestedType}}</p>
            <p><strong>Number of People:</strong> {{accommodation.headCount}}</p>
            <p><strong>Reason for Decline:</strong> {{triggerEvent.notes}}</p>
          </div>
          <p>Please find an alternative accommodation for this client as soon as possible.</p>
          <p><a href="{{BASE_URL}}/reservations/{{reservation.id}}" style="display: inline-block; background-color: #FF5252; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">View Reservation</a></p>
          <p>Best regards,<br>{{companyName}} System</p>
        </div>`
      } else {
        // Default hotel booking request template
        defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>Room Booking Request</h2>
          <p>Dear {{hotel.name}} Team,</p>
          <p>We would like to request a room booking for our client:</p>
          <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
            <p><strong>Client:</strong> {{client.name}}</p>
            <p><strong>Check-in:</strong> {{accommodation.checkInDate}}</p>
            <p><strong>Check-out:</strong> {{accommodation.checkOutDate}}</p>
            <p><strong>Room Type:</strong> {{accommodation.requestedType}}</p>
            <p><strong>Number of People:</strong> {{accommodation.headCount}}</p>
            <p><strong>Special Notes:</strong> {{accommodation.notes}}</p>
          </div>
          <p>Please confirm this booking by clicking the link below:</p>
          <p><a href="https://hooks.tourmanager.pro/email/confirm/{{triggerEvent.id}}" style="display: inline-block; background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Confirm Booking</a></p>
          <p>If you need to decline or have questions, please <a href="https://hooks.tourmanager.pro/email/decline/{{triggerEvent.id}}">click here</a> or reply to this email.</p>
          <p>Thank you for your cooperation.</p>
          <p>Best regards,<br>{{companyName}} Team</p>
        </div>`
      }
    } else if (value === "ReservationService") {
      if (
        formData.templateType === "service-confirmation" ||
        (!templateId && formData.templateType === "service-confirmation")
      ) {
        // Service confirmation template
        defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>Service Provider Response Notification</h2>
          <p>Dear Team Member,</p>
          <p>We have received a response from a service provider regarding a service request:</p>
          <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
            <p><strong>Service:</strong> {{service.name}}</p>
            <p><strong>Provider:</strong> {{provider.name}}</p>
            <p><strong>Response:</strong> <span style="color: #4CAF50; font-weight: bold;">Confirmed</span></p>
            <p><strong>Date:</strong> {{reservationService.date}}</p>
            <p><strong>Time:</strong> {{reservationService.startHour}} - {{reservationService.endHour}}</p>
            <p><strong>Client:</strong> {{client.name}}</p>
            <p><strong>Number of People:</strong> {{reservationService.headCount}}</p>
            <p><strong>Price:</strong> {{triggerEvent.price}}</p>
            <p><strong>Provider Notes:</strong> {{triggerEvent.notes}}</p>
          </div>
          <p>Please update the service state in the system and notify the client if necessary.</p>
          <p><a href="{{BASE_URL}}/reservations/{{reservation.id}}" style="display: inline-block; background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">View Reservation</a></p>
          <p>Best regards,<br>{{companyName}} System</p>
        </div>`
      } else if (
        formData.templateType === "service-declined" ||
        (!templateId && formData.templateType === "service-declined")
      ) {
        // Service declined template  || (!templateId && formData.templateType === "service-declined")) {
        // Service declined template
        defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>Service Provider Response Notification</h2>
          <p>Dear Team Member,</p>
          <p>We have received a response from a service provider regarding a service request:</p>
          <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
            <p><strong>Service:</strong> {{service.name}}</p>
            <p><strong>Provider:</strong> {{provider.name}}</p>
            <p><strong>Response:</strong> <span style="color: #FF5252; font-weight: bold;">Declined</span></p>
            <p><strong>Date:</strong> {{reservationService.date}}</p>
            <p><strong>Time:</strong> {{reservationService.startHour}} - {{reservationService.endHour}}</p>
            <p><strong>Client:</strong> {{client.name}}</p>
            <p><strong>Number of People:</strong> {{reservationService.headCount}}</p>
            <p><strong>Reason for Decline:</strong> {{triggerEvent.notes}}</p>
          </div>
          <p>Please find an alternative service provider for this client as soon as possible.</p>
          <p><a href="{{BASE_URL}}/reservations/{{reservation.id}}" style="display: inline-block; background-color: #FF5252; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">View Reservation</a></p>
          <p>Best regards,<br>{{companyName}} System</p>
        </div>`
      } else {
        // Default service request template
        defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>Service Request</h2>
          <p>Dear {{provider.name}},</p>
          <p>We would like to request your services for our client:</p>
          <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
            <p><strong>Service:</strong> {{service.name}}</p>
            <p><strong>Date:</strong> {{reservationService.date}}</p>
            <p><strong>Time:</strong> {{reservationService.startHour}} - {{reservationService.endHour}}</p>
            <p><strong>Client:</strong> {{client.name}}</p>
            <p><strong>Number of People:</strong> {{reservationService.headCount}}</p>
            <p><strong>Special Notes:</strong> {{reservationService.notes}}</p>
          </div>
          <p>Please confirm this service request and provide your price:</p>
          <p><a href="https://hooks.tourmanager.pro/email/price/{{triggerEvent.id}}" style="display: inline-block; background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Confirm & Set Price</a></p>
          <p>If you need to decline or have questions, please <a href="https://hooks.tourmanager.pro/email/decline/{{triggerEvent.id}}">click here</a> or reply to this email.</p>
          <p>Thank you for your cooperation.</p>
          <p>Best regards,<br>{{companyName}} Team</p>
        </div>`
      }
    }

    // Only update the template content if this is a new template or if we're changing entity types
    const shouldUpdateContent = !templateId || formData.entityType !== value

    setFormData((prev) => ({
      ...prev,
      entityType: value,
      templateType: TEMPLATE_TYPES[value as keyof typeof TEMPLATE_TYPES]?.[0]?.value || "",
      htmlContent: shouldUpdateContent ? defaultHtmlContent : prev.htmlContent,
    }))
  }

  // Update the handleTemplateTypeChange function to update content when template type changes
  const handleTemplateTypeChange = (value: string) => {
    // Only update content for new templates, not when editing existing ones
    if (!templateId) {
      let defaultHtmlContent = formData.htmlContent

      // For hotel templates
      if (formData.entityType === "Accommodation") {
        if (value === "hotel-booking-confirmation") {
          defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>Hotel Booking Response Notification</h2>
            <p>Dear Team Member,</p>
            <p>We have received a response from the hotel regarding a booking request:</p>
            <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
              <p><strong>Hotel:</strong> {{hotel.name}}</p>
              <p><strong>Response:</strong> <span style="color: #4CAF50; font-weight: bold;">Confirmed</span></p>
              <p><strong>Client:</strong> {{client.name}}</p>
              <p><strong>Check-in:</strong> {{accommodation.checkInDate}}</p>
              <p><strong>Check-out:</strong> {{accommodation.checkOutDate}}</p>
              <p><strong>Room Type:</strong> {{accommodation.requestedType}}</p>
              <p><strong>Number of People:</strong> {{accommodation.headCount}}</p>
              <p><strong>Price:</strong> {{triggerEvent.price}}</p>
              <p><strong>Hotel Notes:</strong> {{triggerEvent.notes}}</p>
            </div>
            <p>Please update the reservation status in the system and notify the client if necessary.</p>
            <p><a href="{{BASE_URL}}/reservations/{{reservation.id}}" style="display: inline-block; background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">View Reservation</a></p>
            <p>Best regards,<br>{{companyName}} System</p>
          </div>`
        } else if (value === "hotel-booking-declined") {
          defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>Hotel Booking Response Notification</h2>
            <p>Dear Team Member,</p>
            <p>We have received a response from the hotel regarding a booking request:</p>
            <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
              <p><strong>Hotel:</strong> {{hotel.name}}</p>
              <p><strong>Response:</strong> <span style="color: #FF5252; font-weight: bold;">Declined</span></p>
              <p><strong>Client:</strong> {{client.name}}</p>
              <p><strong>Check-in:</strong> {{accommodation.checkInDate}}</p>
              <p><strong>Check-out:</strong> {{accommodation.checkOutDate}}</p>
              <p><strong>Room Type:</strong> {{accommodation.requestedType}}</p>
              <p><strong>Number of People:</strong> {{accommodation.headCount}}</p>
              <p><strong>Reason for Decline:</strong> {{triggerEvent.notes}}</p>
            </div>
            <p>Please find an alternative accommodation for this client as soon as possible.</p>
            <p><a href="{{BASE_URL}}/reservations/{{reservation.id}}" style="display: inline-block; background-color: #FF5252; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">View Reservation</a></p>
            <p>Best regards,<br>{{companyName}} System</p>
          </div>`
        } else if (value === "hotel-booking-request") {
          defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>Room Booking Request</h2>
            <p>Dear {{hotel.name}} Team,</p>
            <p>We would like to request a room booking for our client:</p>
            <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
              <p><strong>Client:</strong> {{client.name}}</p>
              <p><strong>Check-in:</strong> {{accommodation.checkInDate}}</p>
              <p><strong>Check-out:</strong> {{accommodation.checkOutDate}}</p>
              <p><strong>Room Type:</strong> {{accommodation.requestedType}}</p>
              <p><strong>Number of People:</strong> {{accommodation.headCount}}</p>
              <p><strong>Special Notes:</strong> {{accommodation.notes}}</p>
            </div>
            <p>Please confirm this booking by clicking the link below:</p>
            <p><a href="https://hooks.tourmanager.pro/email/confirm/{{triggerEvent.id}}" style="display: inline-block; background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Confirm Booking</a></p>
            <p>If you need to decline or have questions, please <a href="https://hooks.tourmanager.pro/email/decline/{{triggerEvent.id}}">click here</a> or reply to this email.</p>
            <p>Thank you for your cooperation.</p>
            <p>Best regards,<br>{{companyName}} Team</p>
          </div>`
        }
      }
      // For service templates
      else if (formData.entityType === "ReservationService") {
        if (value === "service-confirmation") {
          defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>Service Provider Response Notification</h2>
            <p>Dear Team Member,</p>
            <p>We have received a response from a service provider regarding a service request:</p>
            <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
              <p><strong>Service:</strong> {{service.name}}</p>
              <p><strong>Provider:</strong> {{provider.name}}</p>
              <p><strong>Response:</strong> <span style="color: #4CAF50; font-weight: bold;">Confirmed</span></p>
              <p><strong>Date:</strong> {{reservationService.date}}</p>
              <p><strong>Time:</strong> {{reservationService.startHour}} - {{reservationService.endHour}}</p>
              <p><strong>Client:</strong> {{client.name}}</p>
              <p><strong>Number of People:</strong> {{reservationService.headCount}}</p>
              <p><strong>Price:</strong> {{triggerEvent.price}}</p>
              <p><strong>Provider Notes:</strong> {{triggerEvent.notes}}</p>
            </div>
            <p>Please update the service state in the system and notify the client if necessary.</p>
            <p><a href="{{BASE_URL}}/reservations/{{reservation.id}}" style="display: inline-block; background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">View Reservation</a></p>
            <p>Best regards,<br>{{companyName}} System</p>
          </div>`
        } else if (value === "service-declined") {
          defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>Service Provider Response Notification</h2>
            <p>Dear Team Member,</p>
            <p>We have received a response from a service provider regarding a service request:</p>
            <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
              <p><strong>Service:</strong> {{service.name}}</p>
              <p><strong>Provider:</strong> {{provider.name}}</p>
              <p><strong>Response:</strong> <span style="color: #FF5252; font-weight: bold;">Declined</span></p>
              <p><strong>Date:</strong> {{reservationService.date}}</p>
              <p><strong>Time:</strong> {{reservationService.startHour}} - {{reservationService.endHour}}</p>
              <p><strong>Client:</strong> {{client.name}}</p>
              <p><strong>Number of People:</strong> {{reservationService.headCount}}</p>
              <p><strong>Reason for Decline:</strong> {{triggerEvent.notes}}</p>
            </div>
            <p>Please find an alternative service provider for this client as soon as possible.</p>
            <p><a href="{{BASE_URL}}/reservations/{{reservation.id}}" style="display: inline-block; background-color: #FF5252; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">View Reservation</a></p>
            <p>Best regards,<br>{{companyName}} System</p>
          </div>`
        } else if (value === "service-request") {
          defaultHtmlContent = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>Service Request</h2>
            <p>Dear {{provider.name}},</p>
            <p>We would like to request your services for our client:</p>
            <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
              <p><strong>Service:</strong> {{service.name}}</p>
              <p><strong>Date:</strong> {{reservationService.date}}</p>
              <p><strong>Time:</strong> {{reservationService.startHour}} - {{reservationService.endHour}}</p>
              <p><strong>Client:</strong> {{client.name}}</p>
              <p><strong>Number of People:</strong> {{reservationService.headCount}}</p>
              <p><strong>Special Notes:</strong> {{reservationService.notes}}</p>
            </div>
            <p>Please confirm this service request and provide your price:</p>
            <p><a href="https://hooks.tourmanager.pro/email/price/{{triggerEvent.id}}" style="display: inline-block; background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Confirm & Set Price</a></p>
            <p>If you need to decline or have questions, please <a href="https://hooks.tourmanager.pro/email/decline/{{triggerEvent.id}}">click here</a> or reply to this email.</p>
            <p>Thank you for your cooperation.</p>
            <p>Best regards,<br>{{companyName}} Team</p>
          </div>`
        }
      }

      setFormData((prev) => ({
        ...prev,
        templateType: value,
        htmlContent: defaultHtmlContent,
      }))
    } else {
      // Just update the template type without changing content for existing templates
      setFormData((prev) => ({ ...prev, templateType: value }))
    }
  }

  const handleEmailContentChange = (html: string) => {
    setFormData((prev) => ({
      ...prev,
      htmlContent: html,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Combine entity type and template type for storage

    if (templateId) {
      updateTemplate({
        id: templateId,
        name: formData.name,
        subject: formData.subject,
        type: formData.entityType,
        htmlContent: formData.htmlContent,
        createdAt: template?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        textContent: formData.textContent
      })
    } 
    else {
      createTemplate({
        name: formData.name,
        subject: formData.subject,
        type: formData.entityType,
        htmlContent: formData.htmlContent,
        textContent: ""
      })
    }

  }

  if (isLoadingTemplate) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          <T tKey="common.back" />
        </Button>
        <h2 className="text-2xl font-bold">
          {templateId ? <T tKey="operations.templates.edit.title" /> : <T tKey="operations.templates.new.title" />}
        </h2>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <LoadingSpinner />
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              <T tKey="common.save" />
            </>
          )}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>
              <T tKey="operations.templates.details" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  <T tKey="operations.templates.fields.name" />
                </Label>
                <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">
                  <T tKey="operations.templates.fields.subject" />
                </Label>
                <Input id="subject" name="subject" value={formData.subject} onChange={handleInputChange} required />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="entityType">
                  <T tKey="operations.templates.fields.entityType" />
                </Label>
                <Select value={formData.entityType} onValueChange={handleEntityTypeChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="templateType">
                  <T tKey="operations.templates.fields.templateType" />
                </Label>
                <Select value={formData.templateType} onValueChange={handleTemplateTypeChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_TYPES[formData.entityType as keyof typeof TEMPLATE_TYPES]?.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <T tKey="operations.templates.content" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EmailBuilder
              initialHtml={formData.htmlContent}
              onChange={handleEmailContentChange}
              entityType={ENTITY_TYPES.find((t) => t.value === formData.entityType)?.label}
              availableFields={availableFields}
            />
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
